import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { applySearchParams } from "~/components/build/Show/lib/urlForView";
import { Job } from "~/components/build/Show/lib/types";
import { getDefaultJobTab, isTabAvailable } from "~/components/job/Job/jobTabs";

type UseJobTabSyncOptions = {
  job: Job | null | undefined;
  enabled?: boolean;
};

type UseJobTabSyncReturn = {
  activeTab: string | null | undefined;
  onTabChange: (tabId: string) => void;
};

/**
 * Derives the active job tab from URL parameters, falling back to the default
 * tab for the given job (annotations if available, otherwise output).
 */
export function useJobTabSync({ job, enabled = true }: UseJobTabSyncOptions): UseJobTabSyncReturn {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useQueryParams();

  // Navigate to a tab in the URL
  const navigateToTab = useCallback(
    (tabId: string, { replace = false } = {}) => {
      const newParams = {
        jobUuid: params.jobUuid,
        stepUuid: params.stepUuid,
        tab: tabId,
      };
      navigate(applySearchParams(pathname, newParams), { replace });
    },
    [navigate, pathname, params.jobUuid, params.stepUuid],
  );

  useEffect(() => {
    if (!enabled || !job) {
      return;
    }

    const defaultTab = getDefaultJobTab(job);

    // If we have a tab in URL, validate it
    if (params.tab) {
      // If the tab is invalid for this job, redirect to the default tab
      if (!isTabAvailable(job, params.tab)) {
        navigateToTab(defaultTab, { replace: true });
      }
      return;
    }

    // No tab in URL — navigate to the default tab
    navigateToTab(defaultTab, { replace: true });
  }, [enabled, job, params.tab, params.jobUuid, params.stepUuid, navigateToTab]);

  // Update URL when tab changes
  const handleTabChange = useCallback(
    (tabId: string) => {
      navigateToTab(tabId);
    },
    [navigateToTab],
  );

  return {
    activeTab: params.tab ?? null,
    onTabChange: handleTabChange,
  };
}
